/**
 * requestContext — contexto enviado como HEADERS nas chamadas à API (porta do bloco de
 * headers do legado: base em toda request e o conjunto estendido do `getMe`
 * [application.js:1986-2005]).
 *
 * Legado:
 *  - TODA request /app/*: source + email + id + fingerprint.
 *  - GET /app/me também: language, timezone, country, region, city, geo, ip, version.
 * Os valores vêm de `session.*` (fingerprint/language do navegador) e de `sync.*` (extensão:
 * version + geo por IP). Sem extensão, o legado gera fingerprint/geo próprios (FPeMiner/
 * getGepIp) — aqui via resolveIdentity/getGeoIp.
 *
 * source/email/id continuam em `auth.ts` (authHeaders). Este módulo cobre o RESTANTE:
 *  - `contextHeaders()`   → { fingerprint } — em toda request.
 *  - `meContextHeaders()` → { language, timezone, country, region, city, geo, ip, version } — só no /me.
 *
 * O contexto é resolvido uma vez no boot (`initRequestContext`, chamado no main.tsx antes do
 * render — como o legado resolve `sync`+fingerprint antes do getMe) e fica num cache SÍNCRONO,
 * além de persistido em localStorage para que reloads o tenham de imediato. Só enviamos chaves
 * com valor (o legado guarda cada header atrás de um `if`).
 */
import i18next from 'i18next';
import { getBrand } from '../config/brand';
import { getCookie } from './cookies';
import { getSync, setSync } from './extensionBridge';
import { getStoredFingerprint, resolveIdentity } from './fingerprint';
import { currentSessionId } from './activity/session';

export interface RequestContext {
  fingerprint?: string;
  language?: string;
  /** versão da extensão (sync.version). */
  version?: string;
  timezone?: string;
  country?: string;
  region?: string;
  city?: string;
  geo?: string;
  ip?: string;
}

/** Chaves de geo vindas do sync/geoip (mesma ordem do legado [1994-1999]). */
const GEO_KEYS = ['timezone', 'country', 'region', 'city', 'geo', 'ip'] as const;

const STORAGE_KEY = 'requestCtx';

let cache: RequestContext | null = null;

/** Grava `key` só se `value` for uma string não-vazia (nunca null/undefined/''). */
function put(target: Record<string, string>, key: string, value: unknown): void {
  if (value == null) return;
  const str = String(value).trim();
  if (str.length > 0) target[key] = str;
}

/** Contexto base síncrono a partir do que já está persistido (fp + idioma) — disponível de
 * imediato mesmo antes do initRequestContext concluir. */
function hydrateFromStorage(): RequestContext {
  const ctx: RequestContext = {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) Object.assign(ctx, JSON.parse(raw) as RequestContext);
  } catch {
    /* storage indisponível/corrompido — segue com o base */
  }
  const fp = getStoredFingerprint();
  if (fp) ctx.fingerprint = fp;
  if (!ctx.language) ctx.language = i18next.language || getBrand().defaultLang;
  return ctx;
}

function persist(ctx: RequestContext): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
  } catch {
    /* storage indisponível */
  }
}

/** Contexto atual (síncrono). Nunca null: cai no hydrate persistido se o boot ainda não rodou. */
export function getRequestContext(): RequestContext {
  return cache ?? hydrateFromStorage();
}

/** Headers de TODA request (além de source/email/id do authHeaders). Legado [1991]. */
export function contextHeaders(): Record<string, string> {
  const ctx = getRequestContext();
  const headers: Record<string, string> = {};
  put(headers, 'fingerprint', ctx.fingerprint);
  // Sessão do registro de uso: os eventos que o servidor emite caem na mesma linha do tempo.
  put(headers, 'X-Activity-Session', currentSessionId());
  return headers;
}

/** Headers EXTRAS do GET /app/me (legado [1992-2000]). Só as chaves com valor. */
export function meContextHeaders(): Record<string, string> {
  const ctx = getRequestContext();
  const headers: Record<string, string> = {};
  put(headers, 'language', ctx.language);
  for (const key of GEO_KEYS) put(headers, key, ctx[key]);
  put(headers, 'version', ctx.version);
  return headers;
}

/** Resolve o contexto completo (extensão + fallback geoip/fingerprint) e atualiza o cache. */
async function resolve(): Promise<void> {
  const ctx: RequestContext = { ...(cache ?? {}) };

  // Dados da extensão (version + geo por IP que ela já capturou), como o legado usa `sync.*`.
  let sync: Record<string, unknown> = {};
  try {
    sync = await Promise.race([
      getSync<Record<string, unknown>>(),
      new Promise<Record<string, unknown>>((r) => setTimeout(() => r({}), 1500)),
    ]);
  } catch {
    sync = {};
  }

  const c = ctx as Record<string, string>;
  put(c, 'version', sync['version']);
  for (const key of GEO_KEYS) put(c, key, sync[key]);
  put(c, 'language', sync['language']);
  if (!ctx.language) ctx.language = i18next.language || getBrand().defaultLang;

  // Sem extensão (ou sem geo/fingerprint no sync): gera fingerprint + geo próprios
  // (FPeMiner/getGepIp [15202/14791]), como o legado faz no boot antes do getMe.
  if (ctx.fingerprint == null || ctx.country == null) {
    try {
      const identity = await resolveIdentity(
        (ctx.fingerprint ?? sync['fingerprint']) as string | undefined,
        (ctx.country ?? sync['country']) as string | undefined,
        getCookie('email') ?? undefined,
      );
      ctx.fingerprint = identity.fingerprint;
      // GeoData.country já é o country_code; mapeia só o que o /me espera.
      put(c, 'ip', identity.geo.ip);
      put(c, 'city', identity.geo.city);
      put(c, 'region', identity.geo.region);
      put(c, 'country', identity.geo.country);
      put(c, 'geo', identity.geo.geo);
      put(c, 'timezone', identity.geo.timezone);
    } catch {
      /* fingerprint/geo são melhor-esforço — segue com o que houver */
    }
  }

  // A captura só inicia se a extensão tiver `sync.fingerprint` [content.js:380]. O bloco acima
  // pode não ter rodado (cache já tinha fingerprint + country), e aí um sync que perdeu a chave
  // — extensão reinstalada, storage limpo — ficaria sem ela para sempre. Reconcilia aqui.
  // Só escreve quando realmente diverge: `storage.sync` tem cota de escrita.
  if (ctx.fingerprint && sync['fingerprint'] !== ctx.fingerprint) {
    setSync({ fingerprint: ctx.fingerprint });
  }

  cache = ctx;
  persist(ctx);
}

/**
 * Inicializa o contexto no boot. Deixa o cache base disponível de imediato (fp + idioma
 * persistidos) e completa em background com extensão/geoip, sob um teto de tempo para nunca
 * travar o boot. Chamado no main.tsx antes do render (fidelidade ao legado).
 */
export async function initRequestContext(): Promise<void> {
  cache = hydrateFromStorage();
  try {
    await Promise.race([resolve(), new Promise<void>((r) => setTimeout(r, 3000))]);
  } catch {
    /* mantém o cache base */
  }
}

/** Para testes: injeta/limpa o cache do contexto. */
export function __setRequestContext(ctx: RequestContext | null): void {
  cache = ctx;
}
